import { containerScoped } from '~/decorators/dependency-container'
import { setUrlParam } from '~/utils/http'
import { inject } from 'tsyringe'
import { CategoryId } from '~/models/category/types'
import { APP_NS } from '~/store/modules/shared/app/state'
import { Store } from 'vuex'
import { RootState } from '~/store/types'
import { releasedFormCategoriesIds } from '~/constants/form'
import { USER_NS } from '~/store/modules/shared/user/state'
import VueRouter from 'vue-router'
import VueI18n from 'vue-i18n'
import { faPlus, faPlusSquare, faUser } from '@fortawesome/free-solid-svg-icons'
import { capitalizeAllWords } from '~/utils/string'
import { ciAudit } from '~/icons/source/regular/audit'
import { AccessToggleName } from '~/models/user/access-toggles'
import DealerSiteService from '~/services/dealers/site/DealerSiteService'

@containerScoped()
export class LegacyUrlService {
  constructor(
    @inject(Store) private store: Store<RootState>,
    @inject(VueRouter) private router: VueRouter,
    @inject(VueI18n) private i18n: VueI18n,
    @inject(DealerSiteService) private dealerSiteService: DealerSiteService
  ) {}

  public getConvertToPartUrl(classifiedId: number): string {
    return `/classifieds/${classifiedId}/legacy-make-part/`
  }

  // function that returns the legacy url for older categories
  public getCompatibilityEditUrl(
    categoryIds: number[],
    classifiedId: number,
    tab?: string
  ) {
    // this indicates if the classified's category id has been released in the new form
    const classifiedCategoryHasReleasedForm = categoryIds.some((id: number) =>
      releasedFormCategoriesIds.includes(id)
    )
    if (classifiedCategoryHasReleasedForm) {
      if (tab) {
        return `/classifieds/${classifiedId}/edit/${tab}/`
      }
      return `/classifieds/${classifiedId}/edit/`
    }

    if (tab) {
      return `/classifieds/${classifiedId}/legacy-edit/#${tab}`
    }
    return `/classifieds/${classifiedId}/legacy-edit/`
  }

  public getRegisterUrl(params?: any) {
    let url = this.router.resolve({ name: '__guest_register' }).href

    const registerReferenceCode =
      this.store.getters[`${USER_NS}/referenceCode`] ||
      (this.store.state.classifieds?.edit?.registerReferenceCode as string)
    if (registerReferenceCode) {
      url = setUrlParam(url, 'refcode', registerReferenceCode)
    }

    for (const prop in params) {
      url = setUrlParam(url, prop, params[prop])
    }

    if (this.dealerSiteService.routeIsOfDealerSite()) {
      url = `https://car.gr${url}`
    }

    return url
  }

  isPlot(): boolean {
    return this.store.getters[`${APP_NS}/isPlot`]
  }

  isGuest(): boolean {
    return this.store.getters[`${USER_NS}/isGuest`]
  }

  isAdmin(): boolean {
    return this.store.getters[`${USER_NS}/isAdmin`]
  }

  isDealer(): boolean {
    return this.store.getters[`${USER_NS}/isOfTypeDealer`]
  }

  isAuditsUser(): boolean {
    return this.store.getters[`${USER_NS}/isAuditsUser`]
  }

  public offerBtnLink() {
    if (this.isAdmin()) {
      return '/admin/users/search/?user-type=manager'
    } else if (this.isAuditsUser()) {
      return this.router.resolve({
        name: '__account_audits_external_certificates'
      }).href
    }
    return this.getNewClassifiedUrl()
  }

  public offerBtnIcon(square: boolean = true) {
    if (this.isAdmin()) {
      return faUser
    } else if (this.isAuditsUser()) {
      return ciAudit
    }
    return square ? faPlusSquare : faPlus
  }

  public offerBtnText(oneWord: boolean = false) {
    if (this.isAdmin()) {
      return this.i18n.t('users') as string
    } else if (this.isDealer()) {
      return oneWord
        ? (this.i18n.t('offer') as string)
        : (this.i18n.t('new classified') as string)
    } else if (this.isAuditsUser()) {
      return oneWord
        ? (this.i18n.t('audits::elegxoi') as string)
        : (this.i18n.t('audits::texnikoi') as string)
    }

    if (oneWord) {
      return this.i18n.t('offer') as string
    }
    return capitalizeAllWords(this.i18n.t('free classified') as string)
  }

  public getGuestRegisterUrl() {
    return '/guests/register/'
  }

  public getNewClassifiedUrl() {
    if (this.isPlot()) {
      return '/classifieds/new/?type=new'
    }
    return '/classifieds/new/?type=new'
  }

  public getDealerRegisterUrl() {
    if (this.isPlot()) {
      return '/register/'
    }
    return '/dealers/register/'
  }

  public getUserLoginUrl() {
    return '/login/'
  }

  public getUserLogoutUrl() {
    return '/logout/'
  }

  public getSearchUrl() {
    if (
      this.store.getters[`${USER_NS}/userHasAccessTo`](
        AccessToggleName.NEW_CATEGORIES_SEARCH_PAGE
      )
    ) {
      return '/quick-search/'
    }
    return '/classifieds/search/'
  }

  public getGuestAvatarEditUrl() {
    if (this.isPlot()) {
      return '/account/'
    }
    return this.router.resolve({ name: '__account' }).href
  }

  public getAccountUrl() {
    return '/account/'
  }

  public getDealerAvatarEditUrl() {
    if (this.isPlot()) {
      return '/account/'
    }
    return '/account/classified_settings/'
  }

  public getContactUrl() {
    return '/information/contact/'
  }

  public getBuyCreditsUrl() {
    return '/account/credits/'
  }

  public getAccountStoresUrl(locator: string = '') {
    return `/account/dealers/${locator}`
  }

  public getMyClassifiedsUrl() {
    return '/classifieds/my/'
  }

  public getUserFaqUrl() {
    if (this.isPlot()) {
      return '/information/faq/users/'
    }
    return '/information/users_faq/'
  }

  public getMyPromotedClassifiedsUrl() {
    return '/classifieds/my/?promotion=1'
  }

  getClientSideLegacyBackendApiBaseUrl(): string | undefined {
    const devLegacyApiBaseUrl = process.env.DEV_LEGACY_BACKEND_API_BASE_URL
    const baseApiUrl = process.env.BACKEND_API_BASE_URL_FROM_CLIENT

    return process.env.NODE_ENV === 'development'
      ? devLegacyApiBaseUrl || baseApiUrl
      : baseApiUrl
  }

  getCompatibilityCopyUrl(categoryIds: number[], classifiedId: number): string {
    const categoryId = categoryIds[categoryIds.length - 1]
    if (categoryIds.includes(CategoryId.JOBS)) {
      return `/jobs/new/${categoryId}/?copy=${classifiedId}`
    } else if (categoryIds.includes(CategoryId.RENTALS)) {
      return `/rentals/new/${categoryId}/?copy=${classifiedId}`
    }
    return `/classifieds/${classifiedId}/legacy-copy/`
  }
}
